import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, Lock } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Modal, useToast } from '@/components/ui';
import { AgentRunPanel } from '@/components/agents/AgentRunPanel';
import { inboxApi } from '@/lib/inboxApi';
import { inboxSyncApi } from '@/lib/inboxSyncApi';
import { connectorsApi } from '@/lib/connectors';
import { agentsApi } from '@/lib/agentsApi';
import { usePlan } from '@/hooks/usePlan';
import { formatLocaleDate } from '@/lib/date';

function relativeTime(iso: string | null, locale = 'en-US'): string {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return formatLocaleDate(d, locale);
}

function sentimentChip(score: number | null, t: (k: string) => string) {
  if (score == null) return null;
  let color = 'bg-ink-100 text-ink-700';
  let label = t('inbox.sentiment_neu');
  if (score > 25) {
    color = 'bg-emerald-50 text-emerald-700';
    label = t('inbox.sentiment_pos');
  } else if (score < -10) {
    color = 'bg-warn-50 text-warn-600';
    label = t('inbox.sentiment_neg');
  }
  return (
    <span
      className={`shrink-0 text-[10px] uppercase tracking-wide font-semibold rounded-md px-1.5 py-0.5 ${color}`}
    >
      <span className="num">{score > 0 ? `+${score}` : score}</span> {label}
    </span>
  );
}

function firstLine(body: string | null): string {
  if (!body) return '';
  const line = body.split(/\r?\n/).find((l) => l.trim().length > 0) ?? '';
  return line.length > 120 ? line.slice(0, 117) + '…' : line;
}

export function InboxPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const plan = usePlan();
  // Inbox sync (the actual Gmail/M365 polling) requires Growth. We still let
  // free users read seeded demo threads — only the live-sync buttons get
  // disabled and the banner explains the upsell.
  const syncLocked = !plan.isLoading && !plan.meets('growth');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const threadsQ = useQuery({
    queryKey: ['inbox-threads', wsId, search],
    queryFn: () =>
      inboxApi.listThreads({
        workspaceId: wsId,
        search: search || undefined,
        limit: 100,
      }),
    enabled: !!wsId,
  });

  const threads = threadsQ.data?.items ?? [];

  const threadQ = useQuery({
    queryKey: ['inbox-thread', selectedId],
    queryFn: () => inboxApi.getThread(selectedId as string),
    enabled: !!selectedId,
  });

  const seedDemo = useMutation({
    mutationFn: () => inboxApi.seedDemo(wsId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox-threads', wsId] }),
  });

  // Detect Gmail + Microsoft 365 installs — drives the per-provider
  // "Sync now" buttons and the connect-inbox hint banner.
  const installsQ = useQuery({
    queryKey: ['connectors-installs', wsId],
    queryFn: () => connectorsApi.listInstalls(wsId),
    enabled: !!wsId,
  });
  const gmailInstall = useMemo(
    () => (installsQ.data ?? []).find((i) => i.provider === 'gmail') ?? null,
    [installsQ.data],
  );
  const m365Install = useMemo(
    () => (installsQ.data ?? []).find((i) => i.provider === 'm365') ?? null,
    [installsQ.data],
  );
  const [syncBanner, setSyncBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const syncMu = useMutation({
    mutationFn: (installId: string) => inboxSyncApi.run(installId),
    onSuccess: () => {
      setSyncBanner({ kind: 'ok', text: t('inbox.sync_succeeded') });
      qc.invalidateQueries({ queryKey: ['inbox-threads', wsId] });
      qc.invalidateQueries({ queryKey: ['connectors-installs', wsId] });
      window.setTimeout(() => setSyncBanner(null), 4000);
    },
    onError: () => {
      setSyncBanner({ kind: 'err', text: t('inbox.sync_failed') });
      window.setTimeout(() => setSyncBanner(null), 6000);
    },
  });

  // The single-provider hint card shows "Sync now" for whichever
  // provider is installed. Both installed → two side-by-side cards
  // below the search bar; neither installed → a single "connect either"
  // banner above.
  const onlyGmail = !!gmailInstall && !m365Install;
  const onlyM365 = !!m365Install && !gmailInstall;
  const bothInstalled = !!gmailInstall && !!m365Install;
  const neitherInstalled =
    !installsQ.isLoading && !gmailInstall && !m365Install;

  // Show a preview of the first message body as the row's secondary line.
  // We don't fetch messages for every thread, so use participants + relative
  // timestamp + an em-dash where preview would be.
  const empty = !threadsQ.isLoading && threads.length === 0;

  const selectedThread = useMemo(
    () => threads.find((t) => t.id === selectedId) ?? threadQ.data?.thread ?? null,
    [threads, selectedId, threadQ.data],
  );

  // Reply Coach — drafts a contextual reply to the latest inbound message
  // in the open thread. We pass the last *inbound* body as the context the
  // agent reacts to (the thread id lets the backend pull full history).
  const [replyOpen, setReplyOpen] = useState(false);
  const lastInboundBody = useMemo(() => {
    const msgs = threadQ.data?.messages ?? [];
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].direction === 'in' && msgs[i].body) return msgs[i].body as string;
    }
    return null;
  }, [threadQ.data]);

  const reply = useMutation({
    mutationFn: () => {
      if (!selectedId || !lastInboundBody) throw new Error('no inbound');
      return agentsApi.replyCoach({
        workspaceId: wsId,
        threadId: selectedId,
        inboundBody: lastInboundBody,
      });
    },
    onError: () => toast.error(t('agents.run_failed')),
  });

  async function copyReplyDraft() {
    const out = reply.data?.output;
    if (!out || typeof out.subject !== 'string' || typeof out.body !== 'string') return;
    try {
      await navigator.clipboard.writeText(`${out.subject}\n\n${out.body}`);
      toast.success(t('inbox.reply_copied'));
    } catch {
      // clipboard blocked — silent
    }
  }

  const replyPromptPreview = selectedId
    ? `thread:${selectedId} reply-coach`
    : undefined;

  return (
    <AppShell titleKey="inbox.title">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-ink-600 max-w-2xl">{t('inbox.intro')}</p>
        {empty && (
          <button
            onClick={() => seedDemo.mutate()}
            disabled={seedDemo.isPending}
            className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100"
          >
            {seedDemo.isPending ? t('inbox.seeding') : t('inbox.seed_button')}
          </button>
        )}
      </div>

      {syncLocked && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2 flex-wrap">
          <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
          <span className="flex-1">{t('plan.locked_inbox_banner')}</span>
          <Link
            to={`/app/workspace/${wsId}/settings#billing`}
            className="font-semibold text-amber-900 hover:text-amber-950 underline shrink-0"
          >
            {t('plan.upgrade_cta', { plan: t('plan.tier_growth') })}
          </Link>
        </div>
      )}

      {neitherInstalled && !syncLocked && (
        <div className="mb-4 rounded-lg border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-700">
          {t('inbox.connect_either_hint')}
        </div>
      )}
      {onlyGmail && !installsQ.isLoading && (
        <div className="mb-4 rounded-lg border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-700">
          {t('inbox.connect_m365_hint')}
        </div>
      )}
      {onlyM365 && !installsQ.isLoading && (
        <div className="mb-4 rounded-lg border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-700">
          {t('inbox.connect_gmail_hint')}
        </div>
      )}

      {bothInstalled && (
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[gmailInstall, m365Install].map((inst) => {
            if (!inst) return null;
            const label = inst.provider === 'gmail' ? 'Gmail' : 'Microsoft 365';
            return (
              <div
                key={inst.id}
                className="rounded-lg border border-ink-200 bg-white px-3 py-2 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink-900">{label}</div>
                  <div className="text-[11px] text-ink-500">
                    {inst.lastSyncedAt ? (
                      <>
                        {t('inbox.last_synced')}{' '}
                        <span className="num">{relativeTime(inst.lastSyncedAt, i18n.language)}</span>
                      </>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => syncMu.mutate(inst.id)}
                  disabled={syncMu.isPending || syncLocked}
                  aria-disabled={syncLocked || undefined}
                  title={syncLocked ? t('plan.locked_inbox_banner') : undefined}
                  className="shrink-0 text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {syncMu.isPending ? t('inbox.syncing') : t('inbox.sync_button')}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {syncBanner && (
        <div
          role="status"
          className={
            'mb-4 rounded-lg px-3 py-2 text-xs ' +
            (syncBanner.kind === 'ok'
              ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border border-warn-200 bg-warn-50 text-warn-700')
          }
        >
          {syncBanner.text}
        </div>
      )}

      {threadsQ.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}

      {empty && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500">
          <p className="text-sm">{t('inbox.empty_title')}</p>
          <p className="text-xs mt-2">{t('inbox.empty_sub')}</p>
        </div>
      )}

      {!empty && !threadsQ.isLoading && (
        <div className="flex border border-ink-200 rounded-xl bg-white overflow-hidden min-h-[60vh]">
          {/* Left pane — thread list */}
          <div className="w-96 shrink-0 border-r border-ink-200 flex flex-col">
            <div className="p-3 border-b border-ink-200 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('inbox.search_placeholder')}
                  aria-label={t('common.search')}
                  className="flex-1 min-w-0 text-sm px-3 py-1.5 border border-ink-200 rounded-md bg-white focus:outline-none focus:border-accent-500"
                />
                {(onlyGmail || onlyM365) && (
                  <button
                    type="button"
                    onClick={() => {
                      const inst = gmailInstall ?? m365Install;
                      if (inst) syncMu.mutate(inst.id);
                    }}
                    disabled={syncMu.isPending || syncLocked}
                    aria-disabled={syncLocked || undefined}
                    className="shrink-0 text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 disabled:opacity-60 disabled:cursor-not-allowed"
                    title={
                      (gmailInstall ?? m365Install)?.lastSyncedAt
                        ? `${t('inbox.last_synced')} ${formatLocaleDate(
                            (gmailInstall ?? m365Install)!.lastSyncedAt as string,
                            i18n.language,
                            { withTime: true },
                          )}`
                        : ''
                    }
                  >
                    {syncMu.isPending ? t('inbox.syncing') : t('inbox.sync_button')}
                  </button>
                )}
              </div>
              {(onlyGmail || onlyM365) && (gmailInstall ?? m365Install)?.lastSyncedAt && (
                <div className="text-[11px] text-ink-500">
                  {t('inbox.last_synced')}{' '}
                  <span className="num">
                    {relativeTime((gmailInstall ?? m365Install)!.lastSyncedAt, i18n.language)}
                  </span>
                </div>
              )}
            </div>
            <ul className="flex-1 overflow-y-auto divide-y divide-ink-200">
              {threads.map((th) => {
                const active = th.id === selectedId;
                return (
                  <li key={th.id}>
                    <button
                      onClick={() => setSelectedId(th.id)}
                      className={
                        'w-full text-left px-4 py-3 transition ' +
                        (active ? 'bg-accent-50' : 'hover:bg-ink-50')
                      }
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div
                          className={
                            'truncate text-sm ' +
                            (active ? 'text-accent-700 font-medium' : 'text-ink-900 font-medium')
                          }
                        >
                          {th.subject || '(no subject)'}
                        </div>
                        <span className="num shrink-0 text-[11px] text-ink-500">
                          {relativeTime(th.lastMessageAt, i18n.language)}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center justify-between gap-2">
                        <div className="truncate text-xs text-ink-500">
                          {th.participants[0] ?? ''}
                        </div>
                        {sentimentChip(th.sentiment, t)}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right pane — selected thread + messages */}
          <div className="flex-1 min-w-0 flex flex-col">
            {!selectedId && (
              <div className="flex-1 flex items-center justify-center text-sm text-ink-500">
                {t('inbox.select_thread')}
              </div>
            )}
            {selectedId && threadQ.isLoading && (
              <div className="p-6 text-sm text-ink-500">{t('common.loading')}</div>
            )}
            {selectedId && selectedThread && (
              <>
                <div className="px-6 py-4 border-b border-ink-200">
                  <h2 className="serif-jp display text-lg text-ink-900">
                    {selectedThread.subject || '(no subject)'}
                  </h2>
                  <div className="mt-1 text-xs text-ink-500 flex items-center gap-2 flex-wrap">
                    <span>{selectedThread.participants.join(' · ')}</span>
                    {sentimentChip(selectedThread.sentiment, t)}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                  {(threadQ.data?.messages ?? []).map((m) => {
                    const isOut = m.direction === 'out';
                    return (
                      <div
                        key={m.id}
                        className={'flex ' + (isOut ? 'justify-end' : 'justify-start')}
                      >
                        <div
                          className={
                            'max-w-[78%] rounded-xl border px-4 py-3 ' +
                            (isOut
                              ? 'border-accent-500 bg-accent-50 text-ink-900'
                              : 'border-ink-200 bg-white text-ink-900')
                          }
                        >
                          <div className="flex items-center gap-2 text-[11px] text-ink-500 mb-1">
                            <span className="truncate">{m.fromAddr}</span>
                            {m.composedByAi && (
                              <span className="shrink-0 text-[9px] uppercase tracking-wide font-semibold rounded-md px-1.5 py-0.5 bg-accent-500 text-white">
                                {t('inbox.ai_badge')}
                              </span>
                            )}
                            <span className="num ml-auto shrink-0">
                              {m.sentAt ? formatLocaleDate(m.sentAt, i18n.language, { withTime: true }) : ''}
                            </span>
                          </div>
                          <div className="text-sm whitespace-pre-wrap leading-relaxed">
                            {m.body}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-ink-200 px-6 py-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      reply.reset();
                      setReplyOpen(true);
                    }}
                    className="text-xs px-3 py-1.5 rounded-md bg-accent-500 text-white hover:bg-accent-600"
                  >
                    {t('inbox.compose_reply')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Modal
        open={replyOpen}
        onOpenChange={(open) => {
          setReplyOpen(open);
          if (!open) reply.reset();
        }}
        size="lg"
        title={t('inbox.reply_modal_title')}
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-600">{t('inbox.reply_intro')}</p>

          {!lastInboundBody && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {t('inbox.reply_no_inbound')}
            </p>
          )}

          {lastInboundBody && !reply.data && !reply.isPending && (
            <button
              type="button"
              onClick={() => reply.mutate()}
              className="inline-flex items-center px-4 py-2 rounded-md bg-ink-900 text-white text-sm font-semibold hover:bg-ink-800"
            >
              {t('inbox.reply_generate')}
            </button>
          )}

          {(reply.data || reply.isPending) && (
            <AgentRunPanel
              response={reply.data}
              prompt={replyPromptPreview}
              isPending={reply.isPending}
            />
          )}

          {reply.data && !reply.isPending && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => reply.mutate()}
                className="inline-flex items-center px-3 py-1.5 rounded-md border border-ink-300 text-sm hover:bg-ink-100"
              >
                {t('inbox.reply_regenerate')}
              </button>
              {reply.data?.output?.subject && (
                <button
                  type="button"
                  onClick={copyReplyDraft}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-ink-300 hover:bg-ink-100"
                >
                  <Copy className="h-3.5 w-3.5" />
                  {t('inbox.reply_copy')}
                </button>
              )}
            </div>
          )}
        </div>
      </Modal>
    </AppShell>
  );
}
